<template>
  <div class="generic-field-popup" :class="{ 
    'datepicker-popup': isDateField, 
    'presets-popup': isPresetField,
    'cascader-popup': isCascaderComponent
  }">
    <!-- Barre de recherche et actions (pour les champs qui le supportent) -->
    <div v-if="supportsSearch" class="popup-header">
      <el-input
          :value="searchValue"
          @input="updateSearch"
          placeholder="Rechercher..."
          size="mini"
          clearable
          prefix-icon="el-icon-search"
          class="search-input"
          @click.native.stop
          @focus.native.stop
          @keydown.native.stop
      />
      <el-button
          v-if="supportsClear"
          size="mini"
          type="text"
          @click="$emit('clear')"
          class="clear-button"
      >
        {{ clearLabel }}
      </el-button>
    </div>

    <!-- Contenu du champ -->
    <div :class="[contentClass, { 'date-popup-content': isDateField }]" :style="contentStyle">
      <!-- Affichage direct pour renderMode: 'list' -->
      <div v-if="fieldConfig.renderMode === 'list'" class="direct-options" :class="{ 'presets-options': isPresetField }">
        <el-checkbox-group
          v-if="fieldConfig.selectionMode === 'multiple'"
          :value="Array.isArray(value) ? value : (value ? [value] : [])"
          @input="handleCheckboxInput"
          size="mini"
        >
          <el-checkbox
            v-for="option in filteredOptions"
            :key="option.value"
            :label="option.value"
            class="option-checkbox"
          >
            {{ option.label }}
          </el-checkbox>
        </el-checkbox-group>

        <el-radio-group v-else :value="value" @input="$emit('input', $event)" size="mini" :class="{ 'presets-radio-group': isPresetField }">
          <el-radio
            v-for="option in filteredOptions"
            :key="option.value"
            :label="option.value"
            :class="['option-radio', { 'preset-radio': isPresetField }]"
          >
            {{ option.label }}
          </el-radio>
        </el-radio-group>
      </div>
      
      <!-- Utilisation des composants SearchField* pour renderMode: 'component' -->
      <component
        v-else-if="fieldConfig.component && fieldConfig.renderMode === 'component'"
        :is="fieldConfig.component"
        :field="fieldConfig"
        :value="getComponentValue()"
        @input="handleComponentInput"
        @close="$emit('close')"
        :business-domain="businessDomain"
        :user-context="userContext"
        :additional-context="additionalContext"
        :search-value="searchValue"
        :is-popup-mode="true"
        class="search-field-popup"
      />
      
      <!-- Fallback si configuration incomplète -->
      <div v-else class="fallback-content">
        <div class="generic-fallback">
          <div style="background: #ffe6e6; padding: 8px; border-radius: 4px; font-size: 12px;">
            <strong>Configuration incomplète:</strong><br>
            Champ: {{ fieldName }}<br>
            Component: {{ fieldConfig.component }}<br>
            RenderMode: {{ fieldConfig.renderMode }}
          </div>
        </div>
      </div>
    </div>

    <!-- Actions spéciales -->
    <div v-if="hasActions && !isDateField" class="popup-actions">
      <el-button v-if="needsCloseButton" size="mini" @click="$emit('close')" type="primary" class="action-close">
        <i class="el-icon-check"></i> Valider
      </el-button>
    </div>
  </div>
</template>

<script>
// Import des composants de champs de recherche
import GenericFieldOfferTypes from '@/components/search/fields/GenericFieldOfferTypes.vue'
import GenericFieldObjectTypes from '@/components/search/fields/GenericFieldObjectTypes.vue'
import GenericFieldSelect from '@/components/search/fields/GenericFieldSelect.vue'
import GenericFieldRadioAdvanced from '@/components/search/fields/GenericFieldRadioAdvanced.vue'
import GenericFieldInput from '@/components/search/fields/GenericFieldInput.vue'
import GenericFieldDateRange from '@/components/search/fields/GenericFieldDateRange.vue'
import GenericFieldRange from '@/components/search/fields/GenericFieldRange.vue'

export default {
  name: 'GenericFieldPopup',
  components: {
    GenericFieldOfferTypes,
    GenericFieldObjectTypes,
    GenericFieldSelect,
    GenericFieldRadioAdvanced,
    GenericFieldInput,
    GenericFieldDateRange,
    GenericFieldRange
  },
  props: {
    fieldName: {
      type: String,
      required: true
    },
    fieldConfig: {
      type: Object,
      required: true
    },
    value: {
      default: null
    },
    formFilters: {
      type: Object,
      default: () => ({})
    },
    userContext: {
      type: Object,
      default: () => ({})
    },
    businessDomain: {
      type: String,
      default: 'immo'
    },
    // Props legacy pour compatibilité
    offerTypes: {
      type: Array,
      default: () => []
    },
    objectTypes: {
      type: Array,
      default: () => []
    },
    stocks: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      searchValue: ''
    }
  },
  computed: {
    //  Détection du type de champ basée sur la configuration backend
    isDateField() {
      return this.fieldConfig.fieldType && this.fieldConfig.fieldType.code === 'daterange'
    },
    
    isPresetField() {
      return this.fieldName === 'preset'
    },
    
    isCascaderComponent() {
      return this.fieldConfig.component === 'SearchFieldObjectTypes'
    },
    
    // Contexte additionnel pour les composants (legacy)
    additionalContext() {
      return {
        stocks: this.stocks,
        offerTypes: this.offerTypes,
        objectTypes: this.objectTypes
      }
    },
    
    // Options normalisées depuis la configuration backend
    normalizedOptions() {
      let source = []
      
      // Priorité 1: dataSource
      if (this.fieldConfig.dataSource && Array.isArray(this.fieldConfig.dataSource.data)) {
        source = this.fieldConfig.dataSource.data
      }
      // Priorité 2: properties.options
      else if (this.fieldConfig.properties && Array.isArray(this.fieldConfig.properties.options)) {
        source = this.fieldConfig.properties.options
      }

      if (!Array.isArray(source)) return []

      return source.map(item => ({
        value: item.value !== undefined ? item.value : (item.code !== undefined ? item.code : item.id),
        label: (item.label && String(item.label).startsWith('general.'))
          ? this.$t(item.label)
          : (item.label !== undefined ? item.label : (item.libelle || item.nom || ''))
      }))
    },

    // Filtrage de recherche
    filteredOptions() {
      let options = this.normalizedOptions
      if (this.searchValue && this.searchValue.trim() !== '') {
        const search = this.searchValue.toLowerCase()
        options = options.filter(o => (o.label || '').toLowerCase().includes(search))
      }
      return options
    },
    
    //  Support de recherche basé sur la configuration backend
    supportsSearch() {
      // Si renderMode = 'list', on peut avoir besoin de recherche pour les longues listes
      if (this.fieldConfig.renderMode === 'list') {
        return this.normalizedOptions.length > 5 // Seuil générique
      }
      
      // Pour les composants, certains ont besoin de recherche externe
      const searchableComponents = ['GenericFieldObjectTypes', 'GenericFieldOfferTypes', 'GenericFieldSelect']
      return searchableComponents.includes(this.fieldConfig.component)
    },
    
    supportsClear() {
      // Seulement pour renderMode = 'list' avec sélection multiple
      return this.fieldConfig.renderMode === 'list' && this.fieldConfig.selectionMode === 'multiple'
    },
    
    clearLabel() {
      return 'Tout décocher'
    },
    
    contentClass() {
      return {
        'popup-content': true,
        'scrollable': this.isScrollable,
        'no-border': this.noBorder
      }
    },
    
    contentStyle() {
      //  TRAITEMENT SPÉCIAL POUR LES CASCADERS basé sur la configuration
      if (this.isCascaderComponent) {
        return { 
          'padding': '0', 
          'overflow': 'visible', 
          'background': 'transparent',
          'border': 'none',
          'min-width': '600px',
          'width': 'auto',
          'max-height': 'none',
          'overflow-y': 'visible'
        }
      }
      
      //  Champs sans bordure/scroll basés sur le type de champ
      if (this.isDateField || this.isPresetField || this.isRangeField) {
        return { 'padding': '8px', 'overflow-x': 'hidden', 'background': '#fff' }
      }
      
      return {
        'max-height': '180px',
        'overflow-y': 'auto',
        'overflow-x': 'hidden',
        'border': 'none',
        'border-radius': '0',
        'padding': '8px',
        'background': '#fff'
      }
    },
    
    isRangeField() {
      return this.fieldConfig.fieldType && this.fieldConfig.fieldType.code === 'slider'
    },
    
    isScrollable() {
      return !this.isDateField && !this.isRangeField
    },
    
    noBorder() {
      return this.isDateField || this.isRangeField
    },
    
    hasActions() {
      return this.needsCloseButton
    },
    
    needsCloseButton() {
      return this.isDateField
    }
  },
  methods: {
    updateSearch(value) {
      this.searchValue = value
      this.$emit('search', { field: this.fieldName, value })
    },
    
    handleCheckboxInput(selectedValues) {
      this.$emit('input', selectedValues)
      this.$emit('change', { field: this.fieldName, value: selectedValues })
    },
    
    //  SIMPLIFIÉ : Obtenir la valeur pour le composant
    getComponentValue() {
      // Pour les champs range, utiliser la configuration eventMapping du backend
      if (this.fieldConfig.eventMapping) {
        const mapping = this.fieldConfig.eventMapping
        if (this.formFilters && (this.formFilters[mapping.minField] || this.formFilters[mapping.maxField])) {
          return {
            [mapping.minField]: this.formFilters[mapping.minField] || null,
            [mapping.maxField]: this.formFilters[mapping.maxField] || null
          }
        }
        return null
      }
      
      // Pour les autres champs, utiliser la valeur directe
      return this.value
    },
    
    //  Gérer les événements basés sur la configuration backend
    handleComponentInput(newValue) {
      // Pour les champs avec eventMapping (range), émettre range-update
      if (this.fieldConfig.eventMapping && newValue && typeof newValue === 'object') {
        const mapping = this.fieldConfig.eventMapping
        
        // Gestion spéciale pour les champs de date (peut être array ou object)
        if (this.isDateField) {
          let startDate, endDate
          
          if (Array.isArray(newValue) && newValue.length === 2) {
            startDate = newValue[0]
            endDate = newValue[1]
          } else if (typeof newValue === 'object' && newValue !== null) {
            startDate = newValue[mapping.minField]
            endDate = newValue[mapping.maxField]
          } else {
            startDate = null
            endDate = null
          }
          
          this.$emit('range-update', {
            field: this.fieldName,
            minField: mapping.minField,
            maxField: mapping.maxField,
            minValue: startDate,
            maxValue: endDate
          })
          return
        }
        
        // Pour les autres champs range (basés sur eventMapping)
        this.$emit('range-update', {
          field: this.fieldName,
          minField: mapping.minField,
          maxField: mapping.maxField,
          minValue: newValue[mapping.minField],
          maxValue: newValue[mapping.maxField]
        })
        return
      }
      
      // Pour les autres champs, émettre directement
      this.$emit('input', newValue)
    }
  }
}
</script>

<style scoped>
/* Styles identiques à l'original mais simplifiés */
.generic-field-popup {
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 8px;
  box-shadow: none;
  position: relative;
  max-width: 380px;
}

.generic-field-popup.datepicker-popup {
  max-width: 450px;
  min-width: 430px;
  width: 440px;
  padding: 0 !important;
}

.generic-field-popup.presets-popup {
  min-width: 340px;
  max-width: 380px;
  width: 360px;
}

.generic-field-popup.cascader-popup {
  width: auto !important;
  min-width: 600px !important;
  max-width: none !important;
}

.date-popup-content {
  padding: 0 !important;
  border: none !important;
  background: transparent !important;
  max-height: none !important;
  overflow: visible !important;
}

.popup-header {
  margin-bottom: 8px;
  display: flex;
  gap: 8px;
}

.search-input {
  font-size: 14px;
  flex: 1;
}

.clear-button {
  font-size: 11px;
  padding: 4px 8px;
  color: #909399;
}

.popup-content.scrollable {
  max-height: 180px;
  overflow-y: auto;
}

.fallback-content {
  width: 100%;
}

.direct-options {
  width: 100%;
}

.option-checkbox,
.option-radio {
  display: block !important;
  margin-bottom: 8px;
  margin-right: 0;
  width: 100%;
}

.option-checkbox .el-checkbox__label,
.option-radio .el-radio__label {
  font-size: 13px;
  line-height: 1.4;
  white-space: normal;
  word-wrap: break-word;
}

.presets-options {
  padding: 6px;
  background: transparent;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
}

.presets-radio-group {
  display: block !important;
  padding: 0;
}

.preset-radio {
  display: block !important;
  margin-bottom: 4px !important;
  line-height: 1.3 !important;
  height: auto !important;
  padding: 2px 0 !important;
  border: none !important;
  background: transparent !important;
}

.preset-radio:last-child {
  margin-bottom: 0 !important;
}

.preset-radio .el-radio__input {
  transform: scale(0.9);
  margin-right: 6px !important;
}

.preset-radio .el-radio__label {
  font-size: 14px !important;
  line-height: 1.3 !important;
  padding-left: 0 !important;
  font-weight: normal !important;
  color: inherit !important;
}

.generic-fallback {
  text-align: center;
  color: #909399;
  font-size: 14px;
  padding: 20px;
}

.popup-actions {
  margin-top: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.action-close {
  font-size: 11px;
}
</style>
